/**
 * Sicherheits-Utilities für das Rating Widget
 * Bot-Schutzmaßnahmen und Sicherheitstoken-Generierung
 */
import { INTERACTION_DELAY_MS } from './constants.js'

// Tracking-Status für Benutzerinteraktionen
const interactionTracking = {
    mouseMovements: 0,
    keyPresses: 0,
    scrollEvents: 0,
    clickEvents: 0,
    startTime: 0,
    delayEnd: 0,
    initialized: false,
    mouseMoveHandler: null,
    keyPressHandler: null,
    scrollHandler: null,
    clickHandler: null,
    debugMode: false,
}

/**
 * Initialisiert das Sicherheits-Tracking mit Verzögerungsoption
 * @param {boolean} debugMode - Ob der Debug-Modus aktiviert ist
 */
export function initSecurity(debugMode = false) {
    if (interactionTracking.initialized) {
        // Wenn bereits initialisiert, nur Debug-Modus aktualisieren
        interactionTracking.debugMode = debugMode
        return
    }

    interactionTracking.startTime = Date.now()
    interactionTracking.delayEnd = interactionTracking.startTime + INTERACTION_DELAY_MS
    interactionTracking.initialized = true
    interactionTracking.debugMode = debugMode

    // Maus-Bewegungs-Tracking
    interactionTracking.mouseMoveHandler = () => {
        interactionTracking.mouseMovements++
    }

    // Tastatur-Tracking
    interactionTracking.keyPressHandler = () => {
        interactionTracking.keyPresses++
    }

    // Scroll-Tracking
    interactionTracking.scrollHandler = () => {
        interactionTracking.scrollEvents++
    }

    // Klick-Tracking
    interactionTracking.clickHandler = () => {
        interactionTracking.clickEvents++
    }

    // Event-Listener registrieren
    document.addEventListener('mousemove', interactionTracking.mouseMoveHandler)
    document.addEventListener('keydown', interactionTracking.keyPressHandler)
    document.addEventListener('scroll', interactionTracking.scrollHandler)
    document.addEventListener('click', interactionTracking.clickHandler)

    // Initial einen Klick registrieren, um die Basisinteraktion sicherzustellen
    interactionTracking.clickEvents++
    // Ein paar Mausbewegungen, um die Grundinteraktion zu simulieren
    interactionTracking.mouseMovements += 5
}

/**
 * Setzt den Debug-Modus für das Sicherheits-Tracking
 * @param {boolean} mode - Debug-Modus aktivieren/deaktivieren
 */
export function setDebugMode(mode) {
    interactionTracking.debugMode = !!mode
}

/**
 * Prüft, ob der Debug-Modus aktiviert ist
 * @returns {boolean} - True wenn Debug-Modus aktiv
 */
export function isDebugMode() {
    return interactionTracking.debugMode
}

/**
 * Startet das aktive Tracking (z.B. wenn ein Benutzer mit dem Widget interagiert)
 * @param {boolean} forceReset - Im Debug-Modus die Tracking-Daten zurücksetzen
 */
export function startTracking(forceReset = false) {
    if (!interactionTracking.initialized) {
        initSecurity(interactionTracking.debugMode)
    } else if (forceReset && interactionTracking.debugMode) {
        // Im Debug-Modus können wir die Interaktionsdaten manuell erhöhen
        interactionTracking.mouseMovements += 10
        interactionTracking.keyPresses += 3
        interactionTracking.clickEvents += 2
    }
}

/**
 * Stoppt das aktive Tracking (z.B. wenn das Modal geschlossen wird)
 */
export function stopTracking() {
    // Tracking nicht beenden, nur für eventuelle Bereinigung
}

/**
 * Bereinigt Event-Listener (für Aufräumarbeiten)
 */
export function cleanupTracking() {
    if (!interactionTracking.initialized) return

    document.removeEventListener('mousemove', interactionTracking.mouseMoveHandler)
    document.removeEventListener('keydown', interactionTracking.keyPressHandler)
    document.removeEventListener('scroll', interactionTracking.scrollHandler)
    document.removeEventListener('click', interactionTracking.clickHandler)

    interactionTracking.initialized = false
}

/**
 * Prüft, ob die initiale Verzögerungsperiode beendet ist
 * @returns {boolean} - True wenn Interaktion erlaubt ist
 */
export function canInteract() {
    return Date.now() >= interactionTracking.delayEnd
}

/**
 * Validiert, ob der Benutzer ein Mensch ist
 * @returns {boolean} - True wenn menschliches Verhalten erkannt wird
 */
export function validateHuman() {
    const timeOnSite = (Date.now() - interactionTracking.startTime) / 1000

    // Wir reduzieren die Anforderungen für kurze Seitenbesuche
    // und erhöhen die Toleranz für Bots (nur wenige Interaktionen nötig)
    const minMovements = timeOnSite < 5 ? 3 : 5
    const minTimeOnSite = 1.5 // 1.5 Sekunden statt 2

    // Grundlegende Validierung: Mausbewegungen und ausreichend Zeit auf der Seite
    // Wir prüfen auch Mausklicks als alternative zu Mausbewegungen
    const basicCheck =
        (interactionTracking.mouseMovements >= minMovements || interactionTracking.clickEvents >= 2) &&
        timeOnSite >= minTimeOnSite

    // Erweiterte Validierung für verdächtiges Verhalten (etwas toleranter)
    const suspiciousBehavior =
        // Extrem viele Aktionen in sehr kurzer Zeit
        (timeOnSite < 3 && interactionTracking.mouseMovements > 200) ||
        (timeOnSite < 3 && interactionTracking.keyPresses > 100)

    return basicCheck && !suspiciousBehavior
}

/**
 * Generiert ein Sicherheitstoken für Anfragen
 * @returns {string} - Das generierte Token
 */
export function generateSecurityToken() {
    const tokenData = {
        t: Date.now(),
        m: interactionTracking.mouseMovements,
        k: interactionTracking.keyPresses,
        s: interactionTracking.scrollEvents,
        c: interactionTracking.clickEvents,
        r: Math.random().toString(36).substring(2),
    }

    // Einfache Tokengenerierung durch Base64-Codierung
    return btoa(JSON.stringify(tokenData))
}
